import React, { useState } from "react";
import { Switch } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ConfirmationModal } from "../../../shared/components/common";
import { ReactComponent as Delete } from "../../../assets/icons/delete-fill.svg";

const DeleteIcon = () => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="max-sm:w-6"
    >
      <path
        d="M9.07151 27.2201C8.35854 27.2201 7.74842 26.9664 7.24114 26.4591C6.73385 25.9518 6.47978 25.3413 6.47891 24.6275V7.77561H5.18262V5.18302H11.6641V3.88672H19.4419V5.18302H25.9234V7.77561H24.6271V24.6275C24.6271 25.3404 24.3734 25.951 23.8661 26.4591C23.3589 26.9673 22.7483 27.2209 22.0345 27.2201H9.07151ZM11.6641 22.0349H14.2567V10.3682H11.6641V22.0349ZM16.8493 22.0349H19.4419V10.3682H16.8493V22.0349Z"
        fill="#F01919"
      />
    </svg>
  );
};

const SecuritySettings = ({
  enforceTLS,
  setEnforceTLS,
  ipWhitelist,
  setIpWhitelist,
  onDeleteIpAddress,
  newIpAddress,
  setNewIpAddress,
  onSaveConfiguration,
  canSaveChanges = true,
  canViewSettings = false,
}) => {
  const [ipToDelete, setIpToDelete] = useState("");

  const handleAddIpAddress = () => {
    if (newIpAddress.trim() && !ipWhitelist.includes(newIpAddress.trim())) {
      setIpWhitelist([...ipWhitelist, newIpAddress.trim()]);
      setNewIpAddress("");
    }
  };

  const handleDeleteIpAddress = () => {
    if (!ipToDelete) {
      return;
    }

    if (typeof onDeleteIpAddress === "function") {
      onDeleteIpAddress(ipToDelete);
    } else {
      setIpWhitelist(ipWhitelist.filter((ip) => ip !== ipToDelete));
    }
    setIpToDelete("");
  };

  const handleDeleteModalClose = () => {
    setIpToDelete("");
  };

  return (
    <div className="bg-white border border-line rounded-2xl p-4 sm:p-6 shadow-sm">
      <div className="mb-6">
        <h2 className="sm:text-lg font-bold text-primary">Security Settings</h2>
        <p className="text-xs text-mutedLight mt-1">
          Configure security options for voicemail delivery
        </p>
      </div>

      {/* Enforce TLS Encryption Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm sm:text-base font-bold text-content">
              Enforce TLS Encryption
            </h3>
            <p className="text-xs text-content mt-1">
              Ensures TLS encryption is established before messages are received
              or sent
            </p>
          </div>
          <Switch
            checked={enforceTLS}
            onChange={(e) => setEnforceTLS(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#011a3c",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#011a3c",
              },
            }}
            disabled={!canViewSettings}
          />
        </div>
      </div>

      {/* IP Whitelist Section */}
      <div className="mb-6">
        <h3 className="text-sm sm:text-base font-bold text-content mb-1">
          IP Whitelist
        </h3>
        <p className="text-xs text-mutedLight mb-4">
          Only accept messages from these IP addresses
        </p>

        {/* Existing IP Addresses */}
        <div className="space-y-3 mb-4">
          {ipWhitelist.map((ip, index) => (
            <div key={index} className="flex items-center gap-3 w-full">
              <div className="bg-line w-full border border-primary rounded-lg px-3 py-2">
                <input
                  type="text"
                  value={ip}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-primary border-none outline-none"
                />
              </div>
              <button
                onClick={() => setIpToDelete(ip)}
                className="p-1 hover:bg-red-50 rounded transition-colors"
                aria-label="Delete IP address"
              >
                <DeleteIcon className="w-4 sm:w-5 h-4 sm:h-5 text-red-500" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            value={newIpAddress}
            onChange={(e) => setNewIpAddress(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleAddIpAddress();
              }
            }}
            disabled={!canViewSettings}
            placeholder="Enter IP Address"
            className="flex-1 px-3 py-2 border border-line rounded-lg text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <button
            onClick={handleAddIpAddress}
            disabled={!canViewSettings}
            className="w-10 h-10 bg-primary text-white rounded-md flex items-center justify-center hover:bg-primary transition-colors shadow-sm"
            aria-label="Add IP address"
          >
            <AddIcon className="w-5 h-5 font-bold" />
          </button>
        </div>
      </div>

      {/* Save Configuration Button */}
      <div className="flex items-center justify-end">
        <button
          onClick={onSaveConfiguration}
          disabled={!canSaveChanges}
          className="bg-primary text-white text-xs sm:text-sm font-bold rounded-md py-3 sm:px-6 px-3 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          SAVE CONFIGURATION
        </button>
      </div>

      <ConfirmationModal
        show={Boolean(ipToDelete)}
        onCancel={handleDeleteModalClose}
        onConfirm={handleDeleteIpAddress}
        title="Delete IP Address"
        messages={[
          `Are you sure you want to delete ${ipToDelete || "this IP address"}?`,
          "This action cannot be undone.",
        ]}
        confirmText="DELETE"
        cancelText="CANCEL"
        confirmColor="error"
        cancelColor="primary"
        IconComponent={Delete}
        iconClassName="w-20 h-24"
      />
    </div>
  );
};

export default SecuritySettings;
